import { useEffect, useRef } from 'react';
import { useTrackSkuView } from '@/useQuery/mutations/useTrackSkuView';
import { useSession } from '@/context/SessionContext';

interface SkuViewTrackerProps {
  skuId: string;
}

// Configuration constants
const MIN_VIEW_TIME = 5000; // 5 seconds
const TRACKING_THRESHOLD = 5 * 60 * 1000; // 5 minutes

export const SkuViewTracker = ({ skuId }: SkuViewTrackerProps) => {
  const { sessionToken } = useSession();
  const { trackSkuViewById: trackSkuViewById } = useTrackSkuView(skuId);
  const lastTrackedTime = useRef<number>(0);
  const viewStartTime = useRef<number>(0);
  const isTracking = useRef<boolean>(false);

  useEffect(() => {
    if (!sessionToken) return;

    const registerSkuView = () => {
      const now = Date.now();
      if (now - lastTrackedTime.current < TRACKING_THRESHOLD) return;

      lastTrackedTime.current = now;
      trackSkuViewById.mutate();
    };

    const startTracking = () => {
      if (isTracking.current) return;
      isTracking.current = true;
      viewStartTime.current = Date.now();
    };

    const stopTracking = () => {
      if (!isTracking.current) return;
      const viewTime = Date.now() - viewStartTime.current;
      if (viewTime >= MIN_VIEW_TIME) {
        registerSkuView();
      }
      isTracking.current = false;
    };

    // Track on mount
    startTracking();

    // Track on page unload
    window.addEventListener('beforeunload', stopTracking);

    // Track on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        stopTracking();
      } else {
        startTracking();
      }
    });

    // Track on scroll
    const handleScroll = () => {
      if (!isTracking.current) {
        startTracking();
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('beforeunload', stopTracking);
      document.removeEventListener('visibilitychange', stopTracking);
      window.removeEventListener('scroll', handleScroll);
      stopTracking();
    };
  }, [skuId, sessionToken, trackSkuViewById]);

  return null;
};
